// src/SuperOrganizations.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from './api';

const emptyOrgForm = {
  id: null,
  name: '',
  legal_name: '',
  slug: '',
  logo_url: '',
  brand_color: '',
  timezone: 'America/Los_Angeles',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  website: '',
  address_street: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  address_country: '',
  tax_id: '',
  billing_email: '',
  billing_notes: '',
  billing_plan: 'basic',
  billing_cycle: 'monthly',
  subscription_status: 'trial',
  trial_end_date: '',
  billing_period_start: '',
  billing_period_end: '',
  max_teachers: '',
  max_students: '',
  max_monthly_visits: '',
  currency: 'USD',
  base_fee: '',
  price_per_teacher: '',
  price_per_student: '',
  invoice_due_days: 10,
  grace_days: 5,
  is_active: 1,
  internal_notes: '',
};

// helper za plan rank – isti kao u BE
const PLAN_RANK = {
  trial: 0,
  basic: 1,
  pro: 2,
  enterprise: 3,
};
function getPlanRank(plan) {
  return PLAN_RANK[plan] ?? 0;
}

// ---- RESTRICTION LEVEL HELPERI NA FE ----
const RESTRICTION_LABELS = {
  normal: {
    label: 'Normal',
    className: 'bg-emerald-100 text-emerald-700',
    description: 'All features are available according to the billing plan.',
  },
  soft_lock: {
    label: 'Soft lock',
    className: 'bg-amber-100 text-amber-700',
    description: 'Some write actions may be limited due to past-due invoices.',
  },
  read_only: {
    label: 'Read-only',
    className: 'bg-orange-100 text-orange-700',
    description: 'Organization can view data, but most write actions are blocked.',
  },
  suspended: {
    label: 'Suspended',
    className: 'bg-red-100 text-red-700',
    description: 'Organization is fully suspended. Users cannot log in.',
  },
};

function getRestrictionLevelFromOrg(orgForm) {
  if (!orgForm) return 'normal';
  if (Number(orgForm.is_active) === 0) return 'suspended';

  const status = orgForm.subscription_status || 'active';
  if (status === 'canceled') return 'read_only';
  if (status === 'past_due') return 'soft_lock';
  return 'normal';
}

function getRestrictionMeta(level) {
  return RESTRICTION_LABELS[level] || RESTRICTION_LABELS.normal;
}

const SuperOrganizations = () => {
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [overview, setOverview] = useState(null);

  const [orgForm, setOrgForm] = useState(emptyOrgForm);
  const [isNew, setIsNew] = useState(false);

  const [settings, setSettings] = useState([]);
  const [settingsDirty, setSettingsDirty] = useState(false);

  const [timezones, setTimezones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);
  const [error, setError] = useState('');
  const [orgSuccessMsg, setOrgSuccessMsg] = useState('');
  const [settingsSuccessMsg, setSettingsSuccessMsg] = useState('');

  // VIŠE admin user-a za org + impersonation + deactivate
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminForm, setAdminForm] = useState({ name: '', email: '' });
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminMsg, setAdminMsg] = useState('');
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);

  // Novo: edit mode + invite/activate status
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [adminSendInviteOnSave, setAdminSendInviteOnSave] = useState(true);
  const [invitingId, setInvitingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  // PLANS + FEATURES
  const [plans, setPlans] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]); // global features iz /super/features
  const [orgFeatureMap, setOrgFeatureMap] = useState({}); // feature_key -> bool (override is_enabled)
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [featuresDirty, setFeaturesDirty] = useState(false);
  const [featuresMsg, setFeaturesMsg] = useState('');
  const [featuresError, setFeaturesError] = useState('');

  // ----- LOAD LISTA ORG -----
  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await API.get('/super/organizations');
      setOrgs(res.data || []);
    } catch (err) {
      console.error('super/organizations FE error', err);
      setError('Failed to load organizations list.');
    } finally {
      setLoading(false);
    }
  };

  // ----- LOAD TIMEZONES -----
  const loadTimezones = async () => {
    try {
      const res = await API.get('/super/timezones');
      setTimezones(res.data || []);
    } catch (err) {
      console.error('super/timezones FE error', err);
    }
  };

  // ----- LOAD SAAS PLANS -----
  const loadPlans = async () => {
    try {
      const res = await API.get('/super/plans');
      setPlans(res.data || []);
    } catch (err) {
      console.error('super/plans FE error', err);
    }
  };

  // ----- LOAD ALL FEATURES DEFINICIJE -----
  const loadAllFeatures = async () => {
    try {
      const res = await API.get('/super/features');
      setAllFeatures(res.data || []);
    } catch (err) {
      console.error('super/features FE error', err);
    }
  };

  // ----- LOAD ADMIN USER(S) ZA ORG -----
  // GET /super/organizations/:orgId/admin-user → vraća NIZ admina: [{ id, name, email, is_active }, ...]
  const loadAdminUsers = async (orgId) => {
    if (!orgId) {
      setAdminUsers([]);
      setAdminForm({ name: '', email: '' });
      setAdminMsg('');
      setEditingAdminId(null);
      setAdminSendInviteOnSave(true);
      setInvitingId(null);
      setActivatingId(null);
      return;
    }
    try {
      const res = await API.get(`/super/organizations/${orgId}/admin-user`);
      const list = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      setAdminUsers(list);
      setAdminForm({ name: '', email: '' });
      setAdminMsg('');
      setEditingAdminId(null);
      setAdminSendInviteOnSave(true);
      setInvitingId(null);
      setActivatingId(null);
    } catch (err) {
      console.error('super/admin-user FE error', err);
      // ne dižemo global error; nije kritično
    }
  };

  // ----- LOAD ORG FEATURE OVERRIDES -----
  // Očekivani BE: GET /api/super/organizations/:orgId/features
  // Response npr: { overrides: [ { feature_key, is_enabled }, ... ] }
  const loadOrgFeatures = async (orgId) => {
    if (!orgId) {
      setOrgFeatureMap({});
      setFeaturesDirty(false);
      setFeaturesMsg('');
      setFeaturesError('');
      return;
    }
    try {
      setFeaturesLoading(true);
      setFeaturesError('');
      setFeaturesMsg('');
      const res = await API.get(`/super/organizations/${orgId}/features`);
      const data = res.data || {};
      const rows = Array.isArray(data) ? data : (data.overrides || []);
      const map = {};
      rows.forEach((row) => {
        if (!row || !row.feature_key) return;
        map[row.feature_key] = row.is_enabled ? true : false;
      });
      setOrgFeatureMap(map);
      setFeaturesDirty(false);
    } catch (err) {
      console.error('super/org features FE error', err);
      setFeaturesError('Failed to load organization feature overrides.');
    } finally {
      setFeaturesLoading(false);
    }
  };

  // ----- LOAD DETAILS (overview + settings) ZA POSTOJEĆU ORG -----
  const loadOrgDetails = async (orgId) => {
    if (!orgId) {
      setOverview(null);
      setSettings([]);
      setSettingsDirty(false);
      setAdminUsers([]);
      setAdminForm({ name: '', email: '' });
      setAdminMsg('');
      setEditingAdminId(null);
      setAdminSendInviteOnSave(true);
      setOrgFeatureMap({});
      setFeaturesDirty(false);
      setFeaturesMsg('');
      setFeaturesError('');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const [ovRes, stRes] = await Promise.all([
        API.get(`/super/organizations/${orgId}/overview`),
        API.get(`/super/organizations/${orgId}/settings`),
      ]);

      const ov = ovRes.data || null;
      setOverview(ov);
      setSettings(stRes.data || []);
      setSettingsDirty(false);

      if (ov && ov.organization) {
        const o = ov.organization;
        setOrgForm({
          id: o.id,
          name: o.name || '',
          legal_name: o.legal_name || '',
          slug: o.slug || '',
          logo_url: o.logo_url || '',
          brand_color: o.brand_color || '',
          timezone: o.timezone || 'America/Los_Angeles',
          contact_name: o.contact_name || '',
          contact_email: o.contact_email || '',
          contact_phone: o.contact_phone || '',
          website: o.website || '',
          address_street: o.address_street || '',
          address_city: o.address_city || '',
          address_state: o.address_state || '',
          address_zip: o.address_zip || '',
          address_country: o.address_country || '',
          tax_id: o.tax_id || '',
          billing_email: o.billing_email || '',
          billing_notes: o.billing_notes || '',
          billing_plan: o.billing_plan || 'basic',
          billing_cycle: o.billing_cycle || 'monthly',
          subscription_status: o.subscription_status || 'active',
          trial_end_date: o.trial_end_date || '',
          billing_period_start: o.billing_period_start || '',
          billing_period_end: o.billing_period_end || '',
          max_teachers: o.max_teachers != null ? String(o.max_teachers) : '',
          max_students: o.max_students != null ? String(o.max_students) : '',
          max_monthly_visits:
            o.max_monthly_visits != null ? String(o.max_monthly_visits) : '',
          currency: o.currency || 'USD',
          base_fee: o.base_fee != null ? String(o.base_fee) : '',
          price_per_teacher:
            o.price_per_teacher != null ? String(o.price_per_teacher) : '',
          price_per_student:
            o.price_per_student != null ? String(o.price_per_student) : '',
          invoice_due_days:
            o.invoice_due_days != null ? Number(o.invoice_due_days) : 10,
          grace_days: o.grace_days != null ? Number(o.grace_days) : 5,
          is_active: o.is_active != null ? Number(o.is_active) : 1,
          internal_notes: o.internal_notes || '',
        });
        setIsNew(false);
      }

      // povuci listu admin user-a
      await loadAdminUsers(orgId);

      // povuci feature override-e
      await loadOrgFeatures(orgId);
    } catch (err) {
      console.error('super/org details FE error', err);
      setError('Failed to load organization details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
    loadTimezones();
    loadPlans();
    loadAllFeatures();
  }, []);

  useEffect(() => {
    if (!isNew && selectedOrgId) {
      loadOrgDetails(selectedOrgId);
    }
  }, [isNew, selectedOrgId]);

  // ----- NEW ORG MODE -----
  const handleNewOrg = () => {
    setSelectedOrgId(null);
    setOverview(null);
    setSettings([]);
    setSettingsDirty(false);
    setOrgForm({
      ...emptyOrgForm,
      timezone: timezones[0] || 'America/Los_Angeles',
    });
    setIsNew(true);
    setError('');
    setOrgSuccessMsg('');
    setSettingsSuccessMsg('');
    setAdminUsers([]);
    setAdminForm({ name: '', email: '' });
    setAdminMsg('');
    setEditingAdminId(null);
    setAdminSendInviteOnSave(true);
    setOrgFeatureMap({});
    setFeaturesDirty(false);
    setFeaturesMsg('');
    setFeaturesError('');
  };

  // ----- SETTINGS EDIT -----
  const handleAddSettingRow = () => {
    if (!selectedOrgId && !isNew) return;
    setSettings((prev) => [
      ...prev,
      { id: null, key_name: '', value_text: '' },
    ]);
    setSettingsDirty(true);
  };

  const handleKeyChange = (index, value) => {
    setSettings((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], key_name: value };
      return copy;
    });
    setSettingsDirty(true);
  };

  const handleValueChange = (index, value) => {
    setSettings((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], value_text: value };
      return copy;
    });
    setSettingsDirty(true);
  };

  const handleSaveSettings = async () => {
    if (!selectedOrgId) return;
    setSettingsSaving(true);
    setError('');
    setSettingsSuccessMsg('');
    try {
      const payload = (settings || [])
        .map((s) => ({
          key_name: (s.key_name || '').trim(),
          value_text: s.value_text ?? '',
        }))
        .filter((s) => s.key_name);

      await API.put(`/super/organizations/${selectedOrgId}/settings`, payload);
      setSettingsDirty(false);
      setSettingsSuccessMsg('Settings saved successfully.');
      await loadOrgDetails(selectedOrgId);
    } catch (err) {
      console.error('super/org save settings FE error', err);
      setError('Failed to save settings.');
    } finally {
      setSettingsSaving(false);
    }
  };

  // ----- ORG FORM EDIT -----
  const handleOrgFieldChange = (field, value) => {
    setOrgForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setOrgSuccessMsg('');
  };

  // primeni default vrednosti plana na limit/ppricing polja
  const handleApplyPlanDefaults = () => {
    const currentPlanKey = orgForm.billing_plan || 'basic';
    const plan = plans.find((p) => p.plan_key === currentPlanKey);
    if (!plan) return;

    setOrgForm((prev) => ({
      ...prev,
      max_teachers:
        plan.default_max_teachers != null
          ? String(plan.default_max_teachers)
          : '',
      max_students:
        plan.default_max_students != null
          ? String(plan.default_max_students)
          : '',
      max_monthly_visits:
        plan.default_max_monthly_visits != null
          ? String(plan.default_max_monthly_visits)
          : '',
      base_fee:
        plan.default_base_fee != null ? String(plan.default_base_fee) : '',
      price_per_teacher:
        plan.default_price_per_teacher != null
          ? String(plan.default_price_per_teacher)
          : '',
      price_per_student:
        plan.default_price_per_student != null
          ? String(plan.default_price_per_student)
          : '',
    }));
    setOrgSuccessMsg('Plan defaults applied to limits & pricing fields (not yet saved).');
  };

  const handleSaveOrg = async () => {
    setOrgSaving(true);
    setError('');
    setOrgSuccessMsg('');
    try {
      const payload = {
        ...orgForm,
        max_teachers:
          orgForm.max_teachers !== '' ? Number(orgForm.max_teachers) : null,
        max_students:
          orgForm.max_students !== '' ? Number(orgForm.max_students) : null,
        max_monthly_visits:
          orgForm.max_monthly_visits !== ''
            ? Number(orgForm.max_monthly_visits)
            : null,
        base_fee: orgForm.base_fee !== '' ? Number(orgForm.base_fee) : null,
        price_per_teacher:
          orgForm.price_per_teacher !== ''
            ? Number(orgForm.price_per_teacher)
            : null,
        price_per_student:
          orgForm.price_per_student !== ''
            ? Number(orgForm.price_per_student)
            : null,
        invoice_due_days: Number(orgForm.invoice_due_days || 10),
        grace_days: Number(orgForm.grace_days || 5),
        is_active: Number(orgForm.is_active || 0),
        currency: (orgForm.currency || 'USD').toUpperCase(),
        trial_end_date: orgForm.trial_end_date || null,
        billing_period_start: orgForm.billing_period_start || null,
        billing_period_end: orgForm.billing_period_end || null,
      };

      let responseOrg;
      if (isNew) {
        const res = await API.post('/super/organizations', payload);
        responseOrg = res.data;
        setIsNew(false);
        setSelectedOrgId(responseOrg.id);
      } else if (orgForm.id) {
        const res = await API.put(
          `/super/organizations/${orgForm.id}`,
          payload
        );
        responseOrg = res.data;
        setSelectedOrgId(responseOrg.id);
      } else {
        throw new Error('Invalid org state.');
      }

      setOrgSuccessMsg('Organization saved successfully.');
      await loadOrganizations();
      if (responseOrg && responseOrg.id) {
        await loadOrgDetails(responseOrg.id);
      }
    } catch (err) {
      console.error('super org save FE error', err);
      const msg =
        err?.response?.data?.error ||
        'Failed to save organization (check required fields and slug uniqueness).';
      setError(msg);
    } finally {
      setOrgSaving(false);
    }
  };

  // ----- ADMIN FORM EDIT -----
  const handleAdminFieldChange = (field, value) => {
    setAdminForm((prev) => ({ ...prev, [field]: value }));
    setAdminMsg('');
  };

  const handleResetAdminForm = () => {
    setEditingAdminId(null);
    setAdminForm({ name: '', email: '' });
    setAdminSendInviteOnSave(true);
    setAdminMsg('');
  };

  const handleEditAdmin = (user) => {
    if (!user) return;
    // ne radimo edit ako je user neaktivan
    if (!user.is_active) return;
    setEditingAdminId(user.id);
    setAdminForm({
      name: user.name || '',
      email: user.email || '',
    });
    // kod edit-a podrazumevano ne šaljemo invite dok ne štikliraš
    setAdminSendInviteOnSave(false);
    setAdminMsg(`Editing admin ${user.email}. Change values below and click "Update admin".`);
  };

  // POST /super/organizations/:orgId/admin-user → kreira/upiše admina
  const handleSaveAdmin = async () => {
    if (!selectedOrgId) return;
    const name = (adminForm.name || '').trim();
    const email = (adminForm.email || '').trim().toLowerCase();

    if (!name || !email) {
      setAdminMsg('Name and email are required.');
      return;
    }

    setAdminSaving(true);
    setAdminMsg('');
    try {
      await API.post(`/super/organizations/${selectedOrgId}/admin-user`, {
        user_id: editingAdminId || null,
        name,
        email,
        send_invite: !!adminSendInviteOnSave,
      });

      await loadAdminUsers(selectedOrgId);

      if (editingAdminId) {
        setAdminMsg(
          adminSendInviteOnSave
            ? 'Admin updated and invite email sent.'
            : 'Admin updated successfully.'
        );
      } else {
        setAdminMsg(
          adminSendInviteOnSave
            ? 'Admin user created and invite email sent.'
            : 'Admin user created.'
        );
      }

      setEditingAdminId(null);
      setAdminForm({ name: '', email: '' });
      setAdminSendInviteOnSave(true);
    } catch (err) {
      console.error('super admin save FE error', err);
      const msg =
        err?.response?.data?.error || 'Failed to save admin user.';
      setAdminMsg(msg);
    } finally {
      setAdminSaving(false);
    }
  };

  const handleSendInviteToAdmin = async (user) => {
    if (!selectedOrgId || !user || !user.id) return;
    if (!user.email) {
      setAdminMsg('This admin has no email set.');
      return;
    }
    if (!user.is_active) {
      setAdminMsg('Cannot send login link to an inactive admin.');
      return;
    }

    setInvitingId(user.id);
    setAdminMsg('');
    try {
      await API.post(`/super/organizations/${selectedOrgId}/admin-user`, {
        user_id: user.id,
        name: user.name || '',
        email: user.email,
        send_invite: true,
      });

      // reload da osvežimo eventualne izmene
      await loadAdminUsers(selectedOrgId);
      setAdminMsg(`Magic login link has been sent to ${user.email}.`);
    } catch (err) {
      console.error('Send invite admin error', err);
      const msg =
        err?.response?.data?.error || 'Failed to send magic login link.';
      setAdminMsg(msg);
    } finally {
      setInvitingId(null);
    }
  };

  const handleActivateAdmin = async (user) => {
    if (!selectedOrgId || !user || !user.id) return;

    const label = user.name || user.email || `#${user.id}`;
    if (
      !window.confirm(
        `Activate admin "${label}"?\nThey will be able to log in again for this organization.`
      )
    ) {
      return;
    }

    setActivatingId(user.id);
    setAdminMsg('');
    try {
      // koristimo istu /admin-user rutu – ona u BE postavlja is_active = 1
      await API.post(`/super/organizations/${selectedOrgId}/admin-user`, {
        user_id: user.id,
        name: user.name || '',
        email: user.email,
        send_invite: false,
      });

      await loadAdminUsers(selectedOrgId);
      setAdminMsg(`Admin "${label}" has been activated.`);
    } catch (err) {
      console.error('Activate admin error', err);
      const msg =
        err?.response?.data?.error || 'Failed to activate admin user.';
      setAdminMsg(msg);
    } finally {
      setActivatingId(null);
    }
  };

  const handleImpersonateAdmin = async (user) => {
    if (!user || !user.id) return;
    setImpersonatingId(user.id);
    setAdminMsg('');

    try {
      const res = await API.post('/super/impersonate', {
        user_id: user.id,
      });
      const { token, user: impersonatedUser } = res.data || {};
      if (!token || !impersonatedUser) {
        throw new Error('Missing token or user in response');
      }

      // 1) Trenutni token/user (može biti super_admin ili već impersonated)
      const currentToken   = localStorage.getItem('token') || '';
      const currentUserRaw = localStorage.getItem('user') || '';

      // 2) Sačuvaj PRVI put – ako već imamo impersonator_*, ne diramo više
      const existingImpToken = localStorage.getItem('impersonator_token');
      const existingImpUser  = localStorage.getItem('impersonator_user');

      if (!existingImpToken || !existingImpUser) {
        localStorage.setItem('impersonator_token', currentToken);
        localStorage.setItem('impersonator_user', currentUserRaw);
      }

      // 3) Upis novog impersonated tokena
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(impersonatedUser));

      // 4) Navigacija – ovde je admin, ali čisto:
      if (impersonatedUser.role === 'admin') {
        navigate('/admin');
      } else if (impersonatedUser.role === 'teacher') {
        navigate('/teacher');
      } else if (impersonatedUser.role === 'parent') {
        navigate('/parent');
      } else {
        // fallback, mada se ovde realno ne dolazi
        navigate('/');
      }
    } catch (err) {
      console.error('Impersonate admin error', err);
      setAdminMsg('Failed to impersonate admin.');
    } finally {
      setImpersonatingId(null);
    }
  };
  

  // POST /super/organizations/:orgId/admin-users/:userId/deactivate
  const handleDeactivateAdmin = async (user) => {
    if (!selectedOrgId || !user || !user.id) return;

    const activeCount = adminUsers.filter((u) => u.is_active).length;
    if (user.is_active && activeCount <= 1) {
      setAdminMsg('At least one admin must remain active for this organization.');
      return;
    }

    const label = user.name || user.email || `#${user.id}`;
    if (
      !window.confirm(
        `Deactivate admin "${label}"?\nThey will no longer be able to log in as an organization admin.`
      )
    ) {
      return;
    }

    setDeactivatingId(user.id);
    setAdminMsg('');
    try {
      await API.post(
        `/super/organizations/${selectedOrgId}/admin-users/${user.id}/deactivate`
      );
      await loadAdminUsers(selectedOrgId);
      setAdminMsg('Admin user deactivated.');
    } catch (err) {
      console.error('Deactivate admin error', err);
      const msg =
        err?.response?.data?.error || 'Failed to deactivate admin user.';
      setAdminMsg(msg);
    } finally {
      setDeactivatingId(null);
    }
  };

  const selectedOrg = !isNew
    ? orgs.find((o) => o.id === selectedOrgId) || null
    : null;

  const invoices = overview?.invoices || [];
  const metrics = overview?.metrics || null;

  const activeAdminsCount = adminUsers.filter((u) => u.is_active).length;

  // ----- FEATURE MATRIX: HELPERI -----
  const currentPlanKey = orgForm.billing_plan || 'basic';
  const currentPlanRank = getPlanRank(currentPlanKey);

  const restrictionLevel = getRestrictionLevelFromOrg(orgForm);
  const restrictionMeta = getRestrictionMeta(restrictionLevel);

  const handleToggleFeature = (feature) => {
    if (!feature || !feature.feature_key) return;
    const key = feature.feature_key;
    const requiredRank = getPlanRank(feature.min_plan || 'basic');
    if (currentPlanRank < requiredRank) {
      // ne dopuštamo toggle ispod plana
      return;
    }

    // Effective ON/OFF trenutno:
    const hasOverride = Object.prototype.hasOwnProperty.call(orgFeatureMap, key);
    const defaultEnabled =
      currentPlanRank >= requiredRank && !!feature.default_enabled;
    const effective = hasOverride ? !!orgFeatureMap[key] : defaultEnabled;

    const next = !effective;

    setOrgFeatureMap((prev) => ({
      ...prev,
      [key]: next,
    }));
    setFeaturesDirty(true);
    setFeaturesMsg('');
  };

  const handleSaveOrgFeatures = async () => {
    if (!selectedOrgId) return;
    setFeaturesSaving(true);
    setFeaturesError('');
    setFeaturesMsg('');
    try {
      const payload = Object.keys(orgFeatureMap).map((k) => ({
        feature_key: k,
        is_enabled: orgFeatureMap[k] ? 1 : 0,
      }));

      // Očekivani BE: PUT /api/super/organizations/:orgId/features
      await API.put(`/super/organizations/${selectedOrgId}/features`, payload);
      setFeaturesDirty(false);
      setFeaturesMsg('Feature overrides saved for this organization.');
      // reload override-a (za svaki slučaj)
      await loadOrgFeatures(selectedOrgId);
    } catch (err) {
      console.error('super/org features save FE error', err);
      setFeaturesError('Failed to save organization features.');
    } finally {
      setFeaturesSaving(false);
    }
  };

  const renderPlanBadge = (planKey) => {
    if (planKey === 'trial') return 'Trial';
    if (planKey === 'basic') return 'Basic';
    if (planKey === 'pro') return 'Pro';
    if (planKey === 'enterprise') return 'Enterprise';
    return planKey;
  };

  return (
    <div className="flex h-full border rounded-xl bg-white overflow-x-auto md:overflow-hidden">
      {/* LEFT: lista organizacija */}
      <div className="w-72 border-r border-gray-200 bg-gray-50 flex flex-col flex-shrink-0">
        <div className="px-3 py-2 border-b bg-white flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">
              Organizations
            </h2>
            <p className="text-[11px] text-gray-400">
              Select or create a tenant.
            </p>
          </div>
          <button
            type="button"
            onClick={handleNewOrg}
            className="px-2 py-1 text-[11px] rounded-lg bg-slate-900 text-white hover:bg-slate-800"
          >
            + New
          </button>
        </div>
        {loading && !orgs.length && (
          <div className="p-3 text-xs text-gray-500">Loading…</div>
        )}
        <div className="flex-1 overflow-y-auto">
          {orgs.map((org) => {
            const active = !isNew && selectedOrgId === org.id;
            return (
              <button
                key={org.id}
                type="button"
                onClick={() => {
                  setIsNew(false);
                  setSelectedOrgId(org.id);
                  setOrgSuccessMsg('');
                  setSettingsSuccessMsg('');
                  setAdminMsg('');
                  setFeaturesMsg('');
                  setFeaturesError('');
                }}
                className={
                  'w-full text-left px-3 py-2 border-b text-xs ' +
                  (active
                    ? 'bg-slate-100 border-slate-200 font-semibold'
                    : 'bg-gray-50 hover:bg-gray-100 border-gray-200')
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <span
                      className={
                        'inline-block w-2 h-2 rounded-full ' +
                        (org.is_active ? 'bg-emerald-500' : 'bg-gray-400')
                      }
                      title={
                        org.is_active
                          ? 'Organization active'
                          : 'Organization inactive'
                      }
                    />
                    <span className="truncate text-gray-800">{org.name}</span>
                  </div>
                  <span
                    className={
                      'ml-2 px-2 py-0.5 rounded-full text-[10px] ' +
                      (org.subscription_status === 'past_due'
                        ? 'bg-red-100 text-red-700'
                        : org.subscription_status === 'trial'
                        ? 'bg-amber-100 text-amber-700'
                        : org.subscription_status === 'canceled'
                        ? 'bg-gray-200 text-gray-600'
                        : 'bg-emerald-100 text-emerald-700')
                    }
                  >
                    {org.subscription_status}
                  </span>
                </div>
                <div className="text-[10px] text-gray-400 truncate">
                  {org.slug} · {org.billing_plan} · {org.timezone}
                </div>
              </button>
            );
          })}
          {!orgs.length && !loading && (
            <div className="p-3 text-xs text-gray-500">
              No organizations found.
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: detalji + settings */}
      <div className="flex-1 flex flex-col min-w-[720px]">
        {error && (
          <div className="m-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}
        {orgSuccessMsg && (
          <div className="mx-3 mb-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
            {orgSuccessMsg}
          </div>
        )}
        {settingsSuccessMsg && (
          <div className="mx-3 mb-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
            {settingsSuccessMsg}
          </div>
        )}

        {!isNew && !selectedOrgId && (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
            Select an organization from the left panel, or create a new one.
          </div>
        )}

        {(isNew || selectedOrgId) && (
          <div className="flex-1 overflow-y-auto p-3 space-y-4">
            {/* ORGANIZATION FORM */}
            <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">
                    {isNew ? 'New organization' : 'Organization details'}
                  </h3>
                  <p className="text-[11px] text-gray-400">
                    Core tenant configuration (name, slug, timezone, billing).
                  </p>
                  {!isNew && (
                    <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px]">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                        Plan: {orgForm.billing_plan || '—'} (
                        {orgForm.billing_cycle || 'monthly'})
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                        Subscription: {orgForm.subscription_status || 'active'}
                      </span>
                      <span
                        className={
                          'px-2 py-0.5 rounded-full border text-[10px] ' +
                          restrictionMeta.className
                        }
                      >
                        {restrictionMeta.label}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSaveOrg}
                  disabled={orgSaving}
                  className={
                    'px-3 py-1.5 text-[11px] rounded-lg font-semibold ' +
                    (orgSaving
                      ? 'bg-gray-300 text-gray-100 cursor-not-allowed'
                      : 'bg-slate-900 text-white hover:bg-slate-800')
                  }
                >
                  {orgSaving ? 'Saving…' : 'Save organization'}
                </button>
              </div>

              {/* Basic info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.name}
                    onChange={(e) =>
                      handleOrgFieldChange('name', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Legal name
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.legal_name}
                    onChange={(e) =>
                      handleOrgFieldChange('legal_name', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Slug * (URL key)
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.slug}
                    onChange={(e) =>
                      handleOrgFieldChange('slug', e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Timezone *
                  </label>
                  <select
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.timezone}
                    onChange={(e) =>
                      handleOrgFieldChange('timezone', e.target.value)
                    }
                  >
                    {timezones.length === 0 && (
                      <option value={orgForm.timezone}>
                        {orgForm.timezone}
                      </option>
                    )}
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Logo URL
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.logo_url}
                    onChange={(e) =>
                      handleOrgFieldChange('logo_url', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Brand color (hex or CSS)
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.brand_color}
                    onChange={(e) =>
                      handleOrgFieldChange('brand_color', e.target.value)
                    }
                  />
                </div>

                {/* Contact */}
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Contact name
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.contact_name}
                    onChange={(e) =>
                      handleOrgFieldChange('contact_name', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Contact email
                  </label>
                  <input
                    type="email"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.contact_email}
                    onChange={(e) =>
                      handleOrgFieldChange('contact_email', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Contact phone
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.contact_phone}
                    onChange={(e) =>
                      handleOrgFieldChange('contact_phone', e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Website
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.website}
                    onChange={(e) =>
                      handleOrgFieldChange('website', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Billing email
                  </label>
                  <input
                    type="email"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.billing_email}
                    onChange={(e) =>
                      handleOrgFieldChange('billing_email', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Tax ID
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.tax_id}
                    onChange={(e) =>
                      handleOrgFieldChange('tax_id', e.target.value)
                    }
                  />
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Address street
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.address_street}
                    onChange={(e) =>
                      handleOrgFieldChange('address_street', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.address_city}
                    onChange={(e) =>
                      handleOrgFieldChange('address_city', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.address_state}
                    onChange={(e) =>
                      handleOrgFieldChange('address_state', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    ZIP
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.address_zip}
                    onChange={(e) =>
                      handleOrgFieldChange('address_zip', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.address_country}
                    onChange={(e) =>
                      handleOrgFieldChange('address_country', e.target.value)
                    }
                  />
                </div>

                {/* Billing/meta */}
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Billing plan
                  </label>
                  <select
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.billing_plan}
                    onChange={(e) =>
                      handleOrgFieldChange('billing_plan', e.target.value)
                    }
                  >
                    <option value="trial">trial</option>
                    <option value="basic">basic</option>
                    <option value="pro">pro</option>
                    <option value="enterprise">enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Billing cycle
                  </label>
                  <select
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.billing_cycle}
                    onChange={(e) =>
                      handleOrgFieldChange('billing_cycle', e.target.value)
                    }
                  >
                    <option value="monthly">monthly</option>
                    <option value="quarterly">quarterly</option>
                    <option value="yearly">yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Subscription status
                  </label>
                  <select
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.subscription_status}
                    onChange={(e) =>
                      handleOrgFieldChange(
                        'subscription_status',
                        e.target.value
                      )
                    }
                  >
                    <option value="trial">trial</option>
                    <option value="active">active</option>
                    <option value="past_due">past_due</option>
                    <option value="canceled">canceled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Trial end date
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.trial_end_date || ''}
                    onChange={(e) =>
                      handleOrgFieldChange('trial_end_date', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Billing period start
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.billing_period_start || ''}
                    onChange={(e) =>
                      handleOrgFieldChange(
                        'billing_period_start',
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Billing period end
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.billing_period_end || ''}
                    onChange={(e) =>
                      handleOrgFieldChange(
                        'billing_period_end',
                        e.target.value
                      )
                    }
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Currency
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.currency}
                    onChange={(e) =>
                      handleOrgFieldChange('currency', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Base fee
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.base_fee}
                    onChange={(e) =>
                      handleOrgFieldChange('base_fee', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Price per teacher
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.price_per_teacher}
                    onChange={(e) =>
                      handleOrgFieldChange(
                        'price_per_teacher',
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Price per student
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.price_per_student}
                    onChange={(e) =>
                      handleOrgFieldChange(
                        'price_per_student',
                        e.target.value
                      )
                    }
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Max teachers
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.max_teachers}
                    onChange={(e) =>
                      handleOrgFieldChange('max_teachers', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Max students
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.max_students}
                    onChange={(e) =>
                      handleOrgFieldChange('max_students', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Max monthly visits
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.max_monthly_visits}
                    onChange={(e) =>
                      handleOrgFieldChange(
                        'max_monthly_visits',
                        e.target.value
                      )
                    }
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Invoice due days
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.invoice_due_days}
                    onChange={(e) =>
                      handleOrgFieldChange(
                        'invoice_due_days',
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Grace days
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.grace_days}
                    onChange={(e) =>
                      handleOrgFieldChange('grace_days', e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Active?
                  </label>
                  <select
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    value={orgForm.is_active}
                    onChange={(e) =>
                      handleOrgFieldChange('is_active', e.target.value)
                    }
                  >
                    <option value={1}>Yes</option>
                    <option value={0}>No</option>
                  </select>
                </div>

                <div className="md:col-span-3">
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Billing notes (internal)
                  </label>
                  <textarea
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    rows={2}
                    value={orgForm.billing_notes}
                    onChange={(e) =>
                      handleOrgFieldChange('billing_notes', e.target.value)
                    }
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-[11px] text-gray-600 mb-1">
                    Internal notes
                  </label>
                  <textarea
                    className="w-full border border-gray-200 rounded px-2 py-1"
                    rows={2}
                    value={orgForm.internal_notes}
                    onChange={(e) =>
                      handleOrgFieldChange('internal_notes', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* PLAN + FEATURE MATRIX (vezano za saas_plans / features / organization_features) */}
            {!isNew && selectedOrgId && (
              <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">
                      Plan & feature matrix
                    </h3>
                    <p className="text-[11px] text-gray-400">
                      Connect <code>billing_plan</code>, limits and{' '}
                      <code>organization_features</code> for this tenant.
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Restriction level:{' '}
                      <span
                        className={
                          'px-1.5 py-0.5 rounded-full ' + restrictionMeta.className
                        }
                      >
                        {restrictionMeta.label}
                      </span>{' '}
                      – {restrictionMeta.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyPlanDefaults}
                    className="px-2 py-1 text-[11px] rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Apply plan defaults
                  </button>
                </div>

                {/* Plan cards */}
                <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
                  {plans.map((p) => {
                    const active = p.plan_key === currentPlanKey;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() =>
                          handleOrgFieldChange('billing_plan', p.plan_key)
                        }
                        className={
                          'flex-1 min-w-[140px] border rounded-lg px-3 py-2 text-left ' +
                          (active
                            ? 'border-sky-500 bg-sky-50'
                            : 'border-gray-200 bg-gray-50 hover:bg-gray-100')
                        }
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-semibold text-gray-800">
                            {p.plan_name} ({renderPlanBadge(p.plan_key)})
                          </span>
                          {active && (
                            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-sky-500 text-white">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-500 mb-1 line-clamp-2">
                          {p.description || '—'}
                        </p>
                        <div className="text-[10px] text-gray-600 space-y-0.5">
                          <div>
                            Max teachers:{' '}
                            <span className="font-semibold">
                              {p.default_max_teachers ?? '—'}
                            </span>
                          </div>
                          <div>
                            Max students:{' '}
                            <span className="font-semibold">
                              {p.default_max_students ?? '—'}
                            </span>
                          </div>
                          <div>
                            Max visits/mo:{' '}
                            <span className="font-semibold">
                              {p.default_max_monthly_visits ?? '—'}
                            </span>
                          </div>
                          <div>
                            Base fee:{' '}
                            <span className="font-semibold">
                              {p.default_base_fee != null
                                ? `$${p.default_base_fee}`
                                : '—'}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Features error / info */}
                {featuresError && (
                  <div className="mb-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                    {featuresError}
                  </div>
                )}
                {featuresMsg && (
                  <div className="mb-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                    {featuresMsg}
                  </div>
                )}

                {/* Features table */}
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="text-[11px] font-semibold text-gray-700">
                      Features for this organization
                    </div>
                    <div className="text-[10px] text-gray-400">
                      Toggle feature access on top of plan defaults
                      (per-org overrides).
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveOrgFeatures}
                    disabled={!featuresDirty || featuresSaving}
                    className={
                      'px-3 py-1.5 text-[11px] rounded-lg font-semibold ' +
                      (!featuresDirty || featuresSaving
                        ? 'bg-gray-300 text-gray-100 cursor-not-allowed'
                        : 'bg-sky-600 text-white hover:bg-sky-700')
                    }
                  >
                    {featuresSaving ? 'Saving…' : 'Save feature overrides'}
                  </button>
                </div>

                {featuresLoading && (
                  <div className="text-[11px] text-gray-500 mb-2">
                    Loading feature overrides…
                  </div>
                )}

                {!allFeatures.length && !featuresLoading && (
                  <p className="text-[11px] text-gray-400">
                    No features defined yet in the <code>features</code> table.
                  </p>
                )}

                {allFeatures.length > 0 && (
                  <div className="mt-1 overflow-x-auto text-[11px] max-h-80 border border-gray-100 rounded-lg">
                    <table className="min-w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left py-1.5 px-2 border-b border-gray-100 w-40">
                            Feature
                          </th>
                          <th className="text-left py-1.5 px-2 border-b border-gray-100">
                            Key
                          </th>
                          <th className="text-left py-1.5 px-2 border-b border-gray-100">
                            Scope
                          </th>
                          <th className="text-left py-1.5 px-2 border-b border-gray-100">
                            Min plan
                          </th>
                          <th className="text-left py-1.5 px-2 border-b border-gray-100">
                            Default
                          </th>
                          <th className="text-center py-1.5 px-2 border-b border-gray-100 w-28">
                            This org
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {allFeatures.map((f) => {
                          const requiredRank = getPlanRank(f.min_plan || 'basic');
                          const defaultEnabled =
                            currentPlanRank >= requiredRank &&
                            !!f.default_enabled;
                          const hasOverride = Object.prototype.hasOwnProperty.call(
                            orgFeatureMap,
                            f.feature_key
                          );
                          const effectiveEnabled = hasOverride
                            ? !!orgFeatureMap[f.feature_key]
                            : defaultEnabled;

                          const locked = currentPlanRank < requiredRank;

                          return (
                            <tr
                              key={f.feature_key}
                              className="border-b last:border-0 hover:bg-gray-50"
                            >
                              <td className="py-1 px-2 align-top">
                                <div className="font-semibold text-gray-800">
                                  {f.name}
                                </div>
                                {f.description && (
                                  <div className="text-[10px] text-gray-500 line-clamp-2">
                                    {f.description}
                                  </div>
                                )}
                              </td>
                              <td className="py-1 px-2 align-top text-gray-600">
                                <code className="text-[10px]">
                                  {f.feature_key}
                                </code>
                              </td>
                              <td className="py-1 px-2 align-top text-gray-600">
                                {f.scope}
                              </td>
                              <td className="py-1 px-2 align-top text-gray-600">
                                <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-[10px]">
                                  {renderPlanBadge(f.min_plan)}
                                </span>
                              </td>
                              <td className="py-1 px-2 align-top text-gray-600">
                                {currentPlanRank < requiredRank ? (
                                  <span className="text-[10px] text-gray-400">
                                    Unavailable on {renderPlanBadge(currentPlanKey)}
                                  </span>
                                ) : defaultEnabled ? (
                                  <span className="text-[10px] text-emerald-700">
                                    Default: ON
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-gray-500">
                                    Default: OFF
                                  </span>
                                )}
                              </td>
                              <td className="py-1 px-2 align-middle text-center">
                                {locked ? (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-400"
                                    title={`Upgrade to ${renderPlanBadge(
                                      f.min_plan
                                    )} to enable this feature.`}
                                  >
                                    Locked
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleFeature(f)}
                                    className={
                                      'inline-flex items-center justify-center w-16 h-6 rounded-full text-[10px] border transition ' +
                                      (effectiveEnabled
                                        ? 'bg-emerald-500 border-emerald-600 text-white'
                                        : 'bg-gray-100 border-gray-300 text-gray-600')
                                    }
                                  >
                                    {effectiveEnabled ? 'ON' : 'OFF'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ORG ADMIN USERS PANEL (za postojeću org) */}
            {!isNew && selectedOrgId && (
              <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">
                      Organization admins
                    </h3>
                    <p className="text-[11px] text-gray-400">
                      Multiple admin users can manage this tenant. At least one admin must remain active.
                    </p>
                  </div>
                  {editingAdminId && (
                    <button
                      type="button"
                      onClick={handleResetAdminForm}
                      className="px-2 py-1 text-[11px] rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Cancel edit
                    </button>
                  )}
                </div>

                {/* Add / edit admin form */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs mb-3">
                  <div>
                    <label className="block text-[11px] text-gray-600 mb-1">
                      Admin name
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-200 rounded px-2 py-1"
                      value={adminForm.name}
                      onChange={(e) =>
                        handleAdminFieldChange('name', e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-600 mb-1">
                      Admin email
                    </label>
                    <input
                      type="email"
                      className="w-full border border-gray-200 rounded px-2 py-1"
                      value={adminForm.email}
                      onChange={(e) =>
                        handleAdminFieldChange('email', e.target.value)
                      }
                    />
                  </div>
                  <div className="flex flex-col items-stretch justify-end gap-1">
                    <button
                      type="button"
                      onClick={handleSaveAdmin}
                      disabled={adminSaving}
                      className={
                        'w-full px-3 py-1.5 text-[11px] rounded-lg font-semibold ' +
                        (adminSaving
                          ? 'bg-gray-300 text-gray-100 cursor-not-allowed'
                          : 'bg-slate-900 text-white hover:bg-slate-800')
                      }
                    >
                      {adminSaving
                        ? 'Saving…'
                        : editingAdminId
                        ? 'Update admin'
                        : 'Create admin'}
                    </button>
                    <label className="inline-flex items-center gap-1 text-[11px] text-gray-600">
                      <input
                        type="checkbox"
                        className="h-3 w-3"
                        checked={adminSendInviteOnSave}
                        onChange={(e) =>
                          setAdminSendInviteOnSave(e.target.checked)
                        }
                      />
                      <span>Send magic login link by email</span>
                    </label>
                  </div>
                </div>

                {/* Lista admin user-a */}
                {adminUsers.length === 0 && (
                  <p className="text-[11px] text-gray-400">
                    No admin users defined yet. Add the first admin above.
                  </p>
                )}

                {adminUsers.length > 0 && (
                  <div className="mt-1 overflow-x-auto text-[11px]">
                    <table className="min-w-full border border-gray-100 rounded-lg overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-1.5 px-2 border-b border-gray-100">
                            Name
                          </th>
                          <th className="text-left py-1.5 px-2 border-b border-gray-100">
                            Email
                          </th>
                          <th className="text-left py-1.5 px-2 border-b border-gray-100">
                            Status
                          </th>
                          <th className="text-right py-1.5 px-2 border-b border-gray-100">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminUsers.map((u) => {
                          const isActive = !!u.is_active;
                          const canDeactivate =
                            isActive && activeAdminsCount > 1;
                          const impersonating = impersonatingId === u.id;
                          const deactivating = deactivatingId === u.id;
                          const activating = activatingId === u.id;
                          const inviting = invitingId === u.id;

                          return (
                            <tr key={u.id} className="border-b last:border-0">
                              <td className="py-1 px-2 align-middle">
                                <span className="font-semibold text-gray-800">
                                  {u.name || '—'}
                                </span>
                              </td>
                              <td className="py-1 px-2 align-middle">
                                <span className="text-gray-700">
                                  {u.email}
                                </span>
                              </td>
                              <td className="py-1 px-2 align-middle">
                                <span
                                  className={
                                    'px-2 py-0.5 rounded-full text-[10px] ' +
                                    (isActive
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-gray-100 text-gray-500')
                                  }
                                >
                                  {isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="py-1 px-2 align-middle text-right">
                                <div className="flex justify-end gap-1 flex-wrap">
                                  <button
                                    type="button"
                                    onClick={() => handleEditAdmin(u)}
                                    disabled={!isActive}
                                    className={
                                      'px-2 py-1 rounded-lg border text-[10px] ' +
                                      (!isActive
                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : editingAdminId === u.id
                                        ? 'bg-slate-800 text-white border-slate-900'
                                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50')
                                    }
                                  >
                                    {editingAdminId === u.id
                                      ? 'Editing'
                                      : 'Edit'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleSendInviteToAdmin(u)
                                    }
                                    disabled={inviting || !u.email || !isActive}
                                    className={
                                      'px-2 py-1 rounded-lg border text-[10px] ' +
                                      (!u.email || inviting || !isActive
                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100')
                                    }
                                  >
                                    {inviting
                                      ? 'Sending…'
                                      : 'Send link'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleImpersonateAdmin(u)}
                                    disabled={!isActive || impersonating}
                                    className={
                                      'px-2 py-1 rounded-lg border text-[10px] ' +
                                      (!isActive || impersonating
                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : 'bg-amber-500/90 text-white border-amber-600 hover:bg-amber-600')
                                    }
                                  >
                                    {impersonating
                                      ? 'Switching…'
                                      : 'Impersonate'}
                                  </button>
                                  {isActive ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleDeactivateAdmin(u)
                                      }
                                      disabled={
                                        !canDeactivate || deactivating
                                      }
                                      className={
                                        'px-2 py-1 rounded-lg border text-[10px] ' +
                                        (!canDeactivate || deactivating
                                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                          : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100')
                                      }
                                    >
                                      {deactivating
                                        ? 'Deactivating…'
                                        : 'Deactivate'}
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleActivateAdmin(u)
                                      }
                                      disabled={activating}
                                      className={
                                        'px-2 py-1 rounded-lg border text-[10px] ' +
                                        (activating
                                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                          : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100')
                                      }
                                    >
                                      {activating
                                        ? 'Activating…'
                                        : 'Activate'}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {activeAdminsCount <= 1 && (
                      <p className="mt-1 text-[10px] text-gray-400">
                        Note: you cannot deactivate the last active admin.
                      </p>
                    )}
                  </div>
                )}

                {adminMsg && (
                  <div className="mt-2 text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                    {adminMsg}
                  </div>
                )}
              </div>
            )}

            {/* Overview & invoices za postojeću org */}
            {!isNew && overview && (
              <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">
                      Usage & invoices
                    </h3>
                    <p className="text-[11px] text-gray-400">
                      Current tenant metrics and last invoices.
                    </p>
                  </div>
                </div>

                {metrics && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2 text-[11px]">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className="text-gray-500">Admins</div>
                      <div className="text-sm font-semibold">
                        {metrics.users.admin_count || 0}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className="text-gray-500">Teachers</div>
                      <div className="text-sm font-semibold">
                        {metrics.users.teacher_count || 0}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className="text-gray-500">Students</div>
                      <div className="text-sm font-semibold">
                        {metrics.students.total_students || 0}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className="text-gray-500">Visits</div>
                      <div className="text-sm font-semibold">
                        {metrics.visits.total_visits || 0}
                      </div>
                    </div>
                  </div>
                )}

                {invoices && invoices.length > 0 && (
                  <div className="mt-2">
                    <div className="text-[11px] font-semibold text-gray-700 mb-1">
                      Recent invoices
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-[11px]">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-1 px-2">Number</th>
                            <th className="text-left py-1 px-2">Period</th>
                            <th className="text-left py-1 px-2">Amount</th>
                            <th className="text-left py-1 px-2">Status</th>
                            <th className="text-left py-1 px-2">Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((inv) => (
                            <tr key={inv.id} className="border-b last:border-0">
                              <td className="py-1 px-2">
                                {inv.invoice_number}
                              </td>
                              <td className="py-1 px-2">
                                {inv.period_start} – {inv.period_end}
                              </td>
                              <td className="py-1 px-2">
                                {inv.amount} {inv.currency}
                              </td>
                              <td className="py-1 px-2">
                                <span
                                  className={
                                    'px-2 py-0.5 rounded-full ' +
                                    (inv.status === 'paid'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : inv.status === 'overdue'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-gray-100 text-gray-700')
                                  }
                                >
                                  {inv.status}
                                </span>
                              </td>
                              <td className="py-1 px-2">
                                {inv.due_date || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Settings editor – samo za postojeću org */}
            {!isNew && selectedOrgId && (
              <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">
                      Organization settings
                    </h3>
                    <p className="text-[11px] text-gray-400">
                      Key/value pairs from <code>system_settings</code>.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleAddSettingRow}
                      className="px-2 py-1 text-[11px] rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      + Add setting
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveSettings}
                      disabled={!settingsDirty || settingsSaving}
                      className={
                        'px-3 py-1.5 text-[11px] rounded-lg font-semibold ' +
                        (!settingsDirty || settingsSaving
                          ? 'bg-gray-300 text-gray-100 cursor-not-allowed'
                          : 'bg-sky-600 text-white hover:bg-sky-700')
                      }
                    >
                      {settingsSaving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </div>

                {!settings.length && (
                  <p className="text-[11px] text-gray-400">
                    No settings defined yet. Add a first key to get started.
                  </p>
                )}

                {settings.length > 0 && (
                  <div className="space-y-2">
                    {settings.map((s, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center border border-gray-100 rounded-lg p-2 text-xs"
                      >
                        {/* LEFT: key_name – sada samo read-only / disabled */}
                        <input
                          type="text"
                          className="border border-gray-200 rounded px-2 py-1 bg-gray-100 text-gray-600"
                          value={s.key_name || ''}
                          readOnly
                          disabled
                        />

                        {/* RIGHT: value_text – i dalje editabilno */}
                        <textarea
                          className="border border-gray-200 rounded px-2 py-1 md:col-span-2"
                          placeholder="value_text"
                          rows={1}
                          value={s.value_text || ''}
                          onChange={(e) =>
                            handleValueChange(index, e.target.value)
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperOrganizations;






